import type { Group } from '../gql';

/**
 * a StaffClass is a special class that acts as a proxy to an org for historical reasons.
 * The heuristic for determining if a class is a staff class is:
 * - if the class type is an organization or district
 * - if the class has a staff class org id
 *
 */
export function isStaffClass(
  group?: Pick<Group, 'type' | 'staff_class_org_id' | 'staffClassOrgId'>
): boolean {
  if (!group) {
    return false;
  }
  const staffClass = getStaffClassOrgId(group);
  return group.type === 'organization' || group.type === 'district' || !!staffClass;
}

export function getStaffClassOrgId(
  group?: Pick<Group, 'staff_class_org_id' | 'staffClassOrgId'>
): number | undefined {
  if (!group) {
    return;
  }
  return group.staff_class_org_id || group.staffClassOrgId || undefined;
}

export function getStaffClassOrgUuid(
  group?: Pick<Group, 'type' | 'staff_class_org_id' | 'staffClassOrgId' | 'organization'>
): string | undefined {
  if (!group) {
    return;
  }
  if (!isStaffClass(group)) {
    return;
  }

  return group.organization?.uuid;
}

/**
 * If it is a regular class return the class uuid but
 * if it is a staff class then return the parent org uuid
 *
 */
export function getOrgUuid(
  group?: Pick<Group, 'type' | 'staff_class_org_id' | 'staffClassOrgId' | 'organization' | 'uuid'>
): string | undefined {
  if (!group) {
    return;
  }
  return isStaffClass(group) ? getStaffClassOrgUuid(group) : group?.uuid ?? '';
}

export function isClass(group?: Pick<Group, 'type'>): boolean {
  if (!group) {
    return false;
  }
  const isOrgOrDistrict = group.type === 'district' || group.type === 'organization';

  return !isOrgOrDistrict;
}
